import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUserEdit } from '@fortawesome/free-solid-svg-icons';
import { clinicsService, isPatientStatus, type Patient } from '../../services/clinicsService';
import { GhostSelect } from '../../components/ui/GhostSelect';
import BtnTaratic from '../../components/ui/BtnTaratic';
import EditorCard from '../../components/ui/EditorCard';

interface ClinicsEditPatientModalProps {
  patient: Patient;
  onClose: () => void;
  onSuccess: () => void;
}

const ClinicsEditPatientModal: React.FC<ClinicsEditPatientModalProps> = ({ patient, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: patient.full_name || '',
    email: patient.email || '',
    phone: patient.phone || '',
    date_of_birth: patient.date_of_birth ? patient.date_of_birth.split('T')[0] : '',
    gender: patient.gender || 'other',
    blood_type: patient.blood_type || '',
    allergies: patient.allergies || '',
    chronic_conditions: patient.chronic_conditions || '',
    emergency_contact_name: patient.emergency_contact_name || '',
    emergency_contact_phone: patient.emergency_contact_phone || '',
    insurance_provider: patient.insurance_provider || '',
    insurance_policy_number: patient.insurance_policy_number || '',
    address: patient.address || '',
    status: patient.status || 'active',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.full_name.trim()) {
      setError(t('clinics.patientForm.errors.nameRequired'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await clinicsService.updatePatient(patient.id, formData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('clinics.patientForm.errors.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-ui="clinics-edit-patient-modal-region-001" className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <EditorCard
        dataUi="clinics-edit-patient-modal-region-002"
        headerDataUi="clinics-edit-patient-modal-region-003"
        bodyDataUi="clinics-edit-patient-modal-region-004"
        footerDataUi="clinics-edit-patient-modal-region-011"
        maxWidth="4xl"
        title={<>{t('clinics.modals.editPatient.title')}: {patient.full_name}</>}
        icon={<FontAwesomeIcon icon={faUserEdit} className="text-blue-400" />}
        onClose={onClose}
        closeLabel={t('common.close', 'Cerrar')}
        footer={(
          <>
            <BtnTaratic type="button" onClick={onClose} color="ghost-dark">
              {t('clinics.modals.newPatient.cancel')}
            </BtnTaratic>
            <BtnTaratic type="submit" form="edit-patient-form" disabled={loading} color="green" className="flex items-center gap-2">
              {loading ? (
                <div data-ui="clinics-edit-patient-modal-region-012" className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faSave} />
              )}
              {t('clinics.modals.editPatient.save')}
            </BtnTaratic>
          </>
        )}
      >
          {error && (
            <div data-ui="clinics-edit-patient-modal-region-005" className="mb-6 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}

          <form id="edit-patient-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Información Básica */}
            <div data-ui="clinics-edit-patient-modal-region-006" className="md:col-span-2 text-lg font-bold text-white border-b border-white/10 pb-2 mb-2 mt-4">
              {t('clinics.patientForm.sections.basicInfo')}
            </div>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.fullName')}</span>
              <input
                type="text"
                required
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.full_name}
                onChange={e => setFormData({ ...formData, full_name: e.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.birthDate')}</span>
              <input
                type="date"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.date_of_birth}
                onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.gender')}</span>
              <GhostSelect
                value={formData.gender}
                onChange={(val: string) => setFormData({ ...formData, gender: val })}
                className="w-full"
                options={[
                  { value: "male", label: t('clinics.patientForm.options.gender.male') },
                  { value: "female", label: t('clinics.patientForm.options.gender.female') },
                  { value: "other", label: t('clinics.patientForm.options.gender.other') }
                ]}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.status')}</span>
              <GhostSelect
                value={formData.status}
                onChange={(val: string) => {
                  if (isPatientStatus(val)) setFormData({ ...formData, status: val });
                }}
                className="w-full"
                options={[
                  { value: "active", label: t('clinics.patientForm.options.status.active') },
                  { value: "inactive", label: t('clinics.patientForm.options.status.inactive') },
                  { value: "discharged", label: t('clinics.patientForm.options.status.discharged') }
                ]}
              />
            </label>

            {/* Datos de Contacto */}
            <div data-ui="clinics-edit-patient-modal-region-007" className="md:col-span-2 text-lg font-bold text-white border-b border-white/10 pb-2 mb-2 mt-4">
              {t('clinics.patientForm.sections.contactInfo')}
            </div>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.email')}</span>
              <input
                type="email"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.phone')}</span>
              <input
                type="tel"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
              />
            </label>

            <label className="md:col-span-2 grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.address')}</span>
              <input
                type="text"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
              />
            </label>

            {/* Datos Médicos */}
            <div data-ui="clinics-edit-patient-modal-region-008" className="md:col-span-2 text-lg font-bold text-white border-b border-white/10 pb-2 mb-2 mt-4">
              {t('clinics.patientForm.sections.medicalData')}
            </div>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.bloodType')}</span>
              <GhostSelect
                value={formData.blood_type}
                onChange={(val: string) => setFormData({ ...formData, blood_type: val })}
                className="w-full"
                options={[
                  { value: "", label: t('clinics.patientForm.options.bloodType.notSpecified') },
                  { value: "A+", label: "A+" },
                  { value: "A-", label: "A-" },
                  { value: "B+", label: "B+" },
                  { value: "B-", label: "B-" },
                  { value: "AB+", label: "AB+" },
                  { value: "AB-", label: "AB-" },
                  { value: "O+", label: "O+" },
                  { value: "O-", label: "O-" }
                ]}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.allergies')}</span>
              <input
                type="text"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.allergies}
                onChange={e => setFormData({ ...formData, allergies: e.target.value })}
              />
            </label>

            <label className="md:col-span-2 grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.chronicConditions')}</span>
              <input
                type="text"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.chronic_conditions}
                onChange={e => setFormData({ ...formData, chronic_conditions: e.target.value })}
              />
            </label>

            {/* Contacto de Emergencia */}
            <div data-ui="clinics-edit-patient-modal-region-009" className="md:col-span-2 text-lg font-bold text-white border-b border-white/10 pb-2 mb-2 mt-4">
              {t('clinics.patientForm.sections.emergencyContact')}
            </div>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.emergencyContactName')}</span>
              <input
                type="text"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.emergency_contact_name}
                onChange={e => setFormData({ ...formData, emergency_contact_name: e.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.emergencyContactPhone')}</span>
              <input
                type="tel"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.emergency_contact_phone}
                onChange={e => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
              />
            </label>

            {/* Seguro Médico */}
            <div data-ui="clinics-edit-patient-modal-region-010" className="md:col-span-2 text-lg font-bold text-white border-b border-white/10 pb-2 mb-2 mt-4">
              {t('clinics.patientForm.sections.insurance')}
            </div>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.insuranceProvider')}</span>
              <input
                type="text"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.insurance_provider}
                onChange={e => setFormData({ ...formData, insurance_provider: e.target.value })}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.insurancePolicy')}</span>
              <input
                type="text"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.insurance_policy_number}
                onChange={e => setFormData({ ...formData, insurance_policy_number: e.target.value })}
              />
            </label>

          </form>
      </EditorCard>
    </div>
  );
};

export default ClinicsEditPatientModal;
